/** Metrics — edit form (RHF + zod + shadcn Form via Refine useForm). */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { updateMetricSchema, type UpdateMetricInput } from "@aos/shared";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { PageHeader } from "@/components/layout/PageHeader";
import {
  FormGrid,
  MarkdownField,
  TextField,
} from "../projects/_form";

/** MetricEdit (D6) — edit form backed by `updateMetricSchema`. */
export function MetricEdit() {
  const form = useForm<UpdateMetricInput, any, UpdateMetricInput>({
    resolver: zodResolver(updateMetricSchema),
    refineCoreProps: { resource: "metrics", action: "edit" },
  });

  const {
    refineCore: { formLoading },
    saveButtonProps,
    control,
  } = form;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Edit metric"
        subtitle="Update the metric's name, unit, description, or owner."
        icon="okrs"
      />
      <Form {...form}>
        <form onSubmit={saveButtonProps.onClick}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Metric details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <>
                  <FormGrid>
                    <TextField control={control} name="name" label="Name" />
                    <TextField
                      control={control}
                      name="unit"
                      label="Unit"
                      placeholder="%, $, count…"
                      description="How the metric is measured."
                    />
                  </FormGrid>
                  <MarkdownField
                    control={control}
                    name="description"
                    label="Description"
                    placeholder="What this metric captures and how to read it."
                  />
                  <TextField
                    control={control}
                    name="owner_user_id"
                    label="Owner"
                    placeholder="user_…"
                    description="The user accountable for this metric."
                  />
                </>
              )}
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="submit" disabled={formLoading}>
                {formLoading ? "Saving…" : "Save changes"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}

export default MetricEdit;
